import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

type StackingPack = {
  title: string;
  entries: number;
};

type SubscriptionStackingExplainerProps = {
  items: StackingPack[];
};

const SubscriptionStackingExplainer = ({
  items
}: SubscriptionStackingExplainerProps) => {
  const examplePack = useMemo(() => {
    const relicPack = items.find((item) => /relic/i.test(item.title));

    if (relicPack) return relicPack;

    return [...items].sort(
      (a, b) => Number(b.entries || 0) - Number(a.entries || 0)
    )[0];
  }, [items]);

  const exampleRows = useMemo(() => {
    const monthlyEntries = Number(examplePack?.entries || 0);
    const bonusEntries = Math.max(0, Math.floor(monthlyEntries * 10));

    return [
      ['Start', bonusEntries, 'bonus entries'],
      [
        'Month 1',
        bonusEntries + monthlyEntries,
        `${bonusEntries.toLocaleString('en-AU')} + ${monthlyEntries.toLocaleString(
          'en-AU'
        )}`
      ],
      [
        'Month 10',
        bonusEntries + monthlyEntries * 10,
        `${bonusEntries.toLocaleString('en-AU')} + ${(
          monthlyEntries * 10
        ).toLocaleString('en-AU')}`
      ]
    ];
  }, [examplePack?.entries]);

  if (!examplePack) return null;

  return (
    <Box
      sx={{
        maxWidth: { xs: 1120, xl: 1440 },
        mx: 'auto',
        mb: { xs: 2.75, md: 3.5 },
        px: { xs: 0.5, sm: 2 }
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1.15fr repeat(3, minmax(0, 1fr))'
          },
          gap: { xs: 1, md: 1.25 },
          alignItems: 'center',
          textAlign: 'center',
          borderRadius: '8px',
          border: '2px solid rgba(8,145,178,0.18)',
          background: 'linear-gradient(135deg, #ffffff 0%, #eefafa 100%)',
          boxShadow: '0 16px 42px rgba(15,23,42,0.08)',
          p: { xs: 1.3, sm: 1.5 }
        }}
      >
        <Box
          sx={{
            px: { xs: 1.1, md: 1.4 },
            py: { xs: 1.1, md: 1.2 },
            minHeight: { md: 120 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              color: '#0f766e',
              fontSize: { xs: 18, md: 20 },
              fontWeight: 950,
              lineHeight: 1.08
            }}
          >
            Entries stack every month
          </Typography>
          <Typography
            sx={{
              mt: 0.55,
              color: '#475569',
              fontSize: { xs: 13, md: 14 },
              fontWeight: 750,
              lineHeight: 1.3
            }}
          >
            Monthly subscription entries accumulate each month, increasing your
            position in each draw.
          </Typography>
          <Typography
            sx={{
              mt: 0.65,
              color: '#64748b',
              fontSize: { xs: 12, md: 13 },
              fontWeight: 850,
              lineHeight: 1.25
            }}
          >
            * Example shown uses {examplePack.title} figures.
          </Typography>
        </Box>

        {exampleRows.map(([label, total, formula]) => (
          <Box
            key={String(label)}
            sx={{
              px: { xs: 1.2, md: 1.4 },
              py: { xs: 1.05, md: 1.2 },
              minHeight: { xs: 58, md: 120 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              borderTop: {
                xs: '1px solid rgba(15,118,110,0.12)',
                md: 0
              },
              borderLeft: {
                xs: 0,
                md: '1px solid rgba(15,118,110,0.16)'
              }
            }}
          >
            <Typography
              sx={{
                color: '#0f172a',
                fontSize: { xs: 14, md: 15 },
                fontWeight: 950,
                lineHeight: 1.1
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                mt: 0.45,
                color: '#0f766e',
                fontSize: { xs: 18, md: 20 },
                fontWeight: 950,
                lineHeight: 1
              }}
            >
              {Number(total).toLocaleString('en-AU')} entries
            </Typography>
            <Typography
              sx={{
                mt: 0.45,
                color: '#64748b',
                fontSize: { xs: 12, md: 13 },
                fontWeight: 800,
                lineHeight: 1.15
              }}
            >
              {formula}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SubscriptionStackingExplainer;
